'use client';

import Link from 'next/link';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Box, Skeleton, Typography } from '@mui/material';

import { useT } from '@/hooks';

import { ROUTES } from '@/constants';

interface PortfolioBalancesProps {
  portfolioValue: number;
  cashBalance: number;
  isLoading?: boolean;
}

interface BalanceCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  isClickable?: boolean;
  href?: string;
  showDivider?: boolean;
  isLoading?: boolean;
}

const BalanceCard = ({
  icon,
  label,
  value,
  isClickable,
  href,
  showDivider = true,
  isLoading,
}: BalanceCardProps) => {
  const content = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: 'fit-content',
        gap: 0.75,
        pr: isClickable && showDivider ? 1 : 0,
        borderRight: isClickable && showDivider ? '1px solid' : 'none',
        borderColor: 'divider',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'opacity 0.2s ease',
        '&:hover': isClickable ? { opacity: 0.8 } : {},
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: '7px',
          bgcolor: 'success.bg',
          color: '#4ec38a',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: 'fit-content' }}>
        <Typography
          sx={{
            fontSize: '0.625rem',
            fontWeight: 500,
            color: 'text.secondary',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 700,
            color: '#4ec38a',
            lineHeight: 1.2,
            mt: 0.25,
            whiteSpace: 'nowrap',
          }}
        >
          {isLoading ? <Skeleton variant="text" width={40} height={16} /> : value}
        </Typography>
      </Box>
    </Box>
  );

  if (isClickable && href) {
    return (
      <Link href={href} style={{ textDecoration: 'none' }}>
        {content}
      </Link>
    );
  }

  return content;
};

export const PortfolioBalances = ({
  portfolioValue,
  cashBalance,
  isLoading,
}: PortfolioBalancesProps) => {
  const { t } = useT();

  const portfolioIcon = (
    <AccountBalanceWalletIcon sx={{ fontSize: { md: '1rem', lg: '1.125rem' } }} />
  );
  const cashIcon = <AttachMoneyIcon sx={{ fontSize: { md: '1rem', lg: '1.125rem' } }} />;

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        width: 'fit-content',
        gap: 1,
        px: 2,
        py: 0.625,
        borderRadius: '10px',
        bgcolor: 'background.subtle',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: 'background.hover',
        },
      }}
    >
      <BalanceCard
        icon={portfolioIcon}
        label={t('nav.portfolio')}
        value={`$${portfolioValue.toFixed(2)}`}
        isClickable={!isLoading}
        href={ROUTES.PORTFOLIO}
        isLoading={isLoading}
      />
      <BalanceCard
        icon={cashIcon}
        label={t('nav.cash')}
        value={`$${cashBalance.toFixed(2)}`}
        isClickable={!isLoading}
        href={ROUTES.PORTFOLIO}
        showDivider={false}
        isLoading={isLoading}
      />
    </Box>
  );
};
